// Bulk add leads to a pipeline stage. Open from the Pipeline header.
// Shows a searchable + status-filterable lead list with checkboxes; the
// target stage is picked via radio chips. Leads already in this pipeline
// get a "in: <stage>" badge so the user understands what mode='move' does.
//
// Server endpoint: POST /pipelines/:id/leads/bulk. Returns a summary
// ({ added, moved, skipped, notFound }) which we surface as a toast.

import React, { useEffect, useMemo, useState } from "react";
import {
  Search, Check, Loader2, AlertCircle, X, GitBranch,
} from "lucide-react";
import toast from "react-hot-toast";
import { Select as AntSelect } from "antd";
import Modal from "../overlays/Modal";
import { Button, Badge } from "../atoms";
import { useLeads } from "../hooks/useLeads";
import { useBulkAddLeads } from "../hooks/usePipelines";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { formatCurrency as libFormatCurrency } from "../lib/currency";

const STATUS_OPTIONS = [
  { id: "open", label: "Open" },
  { id: "won", label: "Won" },
  { id: "lost", label: "Lost" },
];

function formatErr(err) {
  return err?.response?.data?.error || err?.message || "Request failed";
}

// Build leadId -> stageName for the active pipeline so each row shows
// where the lead already lives (if anywhere).
function buildLocationMap(pipeline) {
  const map = new Map();
  if (!pipeline?.stages) return map;
  for (const s of pipeline.stages) {
    for (const e of s.leads || []) {
      map.set(String(e.leadId), s.name);
    }
  }
  return map;
}

function LeadRow({ lead, checked, onToggle, existingStage, orgCurrency }) {
  return (
    <label
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        padding: "10px 14px",
        borderBottom: "1px solid var(--color-border-subtle)",
        cursor: "pointer",
        background: checked ? "var(--crm-primary-soft)" : "transparent",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        onChange={() => onToggle(lead._id)}
        style={{ width: 16, height: 16, cursor: "pointer", accentColor: "var(--crm-primary)" }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 500, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {lead.title}
        </div>
        <div style={{ fontSize: 11, color: "var(--color-text-secondary)", display: "flex", gap: 6, marginTop: 2, flexWrap: "wrap" }}>
          {lead.brandName && <span>{lead.brandName}</span>}
          {lead.status && <span>· {lead.status}</span>}
          {lead.value > 0 && <span>· {libFormatCurrency(lead.value, lead.currency || orgCurrency)}</span>}
        </div>
      </div>
      {existingStage && (
        <Badge tone="warning">in: {existingStage}</Badge>
      )}
    </label>
  );
}

export default function BulkAddLeadsModal({ open, onClose, pipeline }) {
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("open");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [targetStageId, setTargetStageId] = useState(null);
  const [mode, setMode] = useState("skip-existing");
  const { currency: orgCurrency } = useOrgCurrency();

  const { data, isLoading, isError, error } = useLeads({ query, status: statusFilter });
  const bulkAdd = useBulkAddLeads();

  const leads = data?.items || [];
  const total = data?.total ?? leads.length;
  const locationMap = useMemo(() => buildLocationMap(pipeline), [pipeline]);
  const stages = pipeline?.stages || [];

  // Reset state on open/pipeline change.
  useEffect(() => {
    if (!open) return;
    setQuery("");
    setStatusFilter("open");
    setSelectedIds(new Set());
    setMode("skip-existing");
    setTargetStageId(stages[0]?._id || null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, pipeline?._id]);

  const toggle = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const visibleIds = leads.map((l) => l._id);
  const allVisibleChecked = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleChecked = visibleIds.some((id) => selectedIds.has(id));

  const toggleAllVisible = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleChecked) visibleIds.forEach((id) => next.delete(id));
      else visibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const submit = async () => {
    if (!targetStageId) { toast.error("Pick a target stage"); return; }
    if (selectedIds.size === 0) { toast.error("Pick at least one lead"); return; }
    try {
      const res = await bulkAdd.mutateAsync({
        pipelineId: pipeline._id,
        leadIds: [...selectedIds],
        stageId: targetStageId,
        mode,
      });
      const s = res?.summary || {};
      const parts = [];
      if (s.added) parts.push(`${s.added} added`);
      if (s.moved) parts.push(`${s.moved} moved`);
      if (s.skipped) parts.push(`${s.skipped} skipped`);
      if (s.notFound) parts.push(`${s.notFound} not found`);
      toast.success(parts.length ? parts.join(" · ") : "Done");
      onClose?.();
    } catch (err) {
      toast.error(formatErr(err));
    }
  };

  if (!pipeline) return null;

  return (
    <Modal open={open} onClose={onClose} width={680}>
      <div style={{ display: "flex", flexDirection: "column", maxHeight: "calc(100vh - 160px)" }}>
        {/* Header */}
        <div style={{ padding: "16px 20px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "var(--crm-primary-soft)", color: "var(--crm-primary)", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <GitBranch size={16} />
            </div>
            <div>
              <h2 style={{ fontSize: 16, fontWeight: 600, margin: 0 }}>Add leads to pipeline</h2>
              <div style={{ fontSize: 12, color: "var(--color-text-secondary)", marginTop: 2 }}>{pipeline.name}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: "transparent", border: "none", color: "var(--color-text-secondary)", cursor: "pointer", padding: 4, display: "flex" }} aria-label="Close">
            <X size={18} />
          </button>
        </div>

        {/* Stage picker */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--color-border-subtle)" }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: "var(--color-text-secondary)", marginBottom: 8 }}>
            Target stage
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {stages.map((s) => {
              const active = s._id === targetStageId;
              return (
                <button
                  key={s._id}
                  onClick={() => setTargetStageId(s._id)}
                  style={{
                    height: 30,
                    padding: "0 12px",
                    borderRadius: 999,
                    border: "1px solid",
                    borderColor: active ? "var(--crm-primary)" : "var(--color-border-subtle)",
                    background: active ? "var(--crm-primary)" : "var(--color-bg-surface)",
                    color: active ? "#fff" : "var(--color-text-secondary)",
                    fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    cursor: "pointer",
                  }}
                >
                  {s.name}
                </button>
              );
            })}
            {stages.length === 0 && (
              <div style={{ fontSize: 12, color: "var(--crm-danger)" }}>This pipeline has no stages yet.</div>
            )}
          </div>
        </div>

        {/* Search + status + select-all */}
        <div style={{ padding: "12px 20px", borderBottom: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 240px", minWidth: 200 }}>
            <Search size={14} style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "var(--color-text-secondary)" }} />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search leads…"
              style={{ width: "100%", height: 32, padding: "0 10px 0 32px", borderRadius: 8, border: "1px solid var(--color-border-subtle)", background: "var(--color-bg-surface)", color: "var(--color-text-primary)", fontSize: 13, outline: "none" }}
            />
          </div>
          <AntSelect
            value={statusFilter || undefined}
            onChange={(v) => setStatusFilter(v || "")}
            placeholder="All statuses"
            allowClear
            options={STATUS_OPTIONS.map((s) => ({ value: s.id, label: s.label }))}
            style={{ width: 150, height: 32 }}
          />
          <button
            onClick={toggleAllVisible}
            disabled={visibleIds.length === 0}
            style={{
              height: 32,
              padding: "0 12px",
              borderRadius: 8,
              border: "1px solid var(--color-border-subtle)",
              background: "var(--color-bg-surface)",
              color: "var(--color-text-primary)",
              fontSize: 13,
              fontWeight: 500,
              cursor: visibleIds.length === 0 ? "not-allowed" : "pointer",
              opacity: visibleIds.length === 0 ? 0.5 : 1,
              display: "flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            {allVisibleChecked ? <Check size={13} /> : null}
            {allVisibleChecked ? "Unselect visible" : "Select visible"}
          </button>
        </div>

        {/* Lead list */}
        <div style={{ flex: 1, overflowY: "auto", minHeight: 240, maxHeight: 360 }}>
          {isLoading && (
            <div style={{ display: "flex", alignItems: "center", gap: 8, padding: 24, justifyContent: "center", color: "var(--color-text-secondary)" }}>
              <Loader2 size={16} className="animate-spin" /> Loading leads…
            </div>
          )}
          {isError && (
            <div style={{ padding: 16, color: "var(--crm-danger)", display: "flex", alignItems: "center", gap: 8 }}>
              <AlertCircle size={14} /> {formatErr(error)}
            </div>
          )}
          {!isLoading && !isError && leads.length === 0 && (
            <div style={{ padding: 32, textAlign: "center", color: "var(--color-text-secondary)", fontSize: 13 }}>
              No leads match this filter.
            </div>
          )}
          {!isLoading && !isError && leads.map((l) => (
            <LeadRow
              key={l._id}
              lead={l}
              checked={selectedIds.has(l._id)}
              onToggle={toggle}
              existingStage={locationMap.get(String(l._id))}
              orgCurrency={orgCurrency}
            />
          ))}
        </div>

        {/* Footer */}
        <div style={{ padding: "12px 20px", borderTop: "1px solid var(--color-border-subtle)", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
          <div style={{ fontSize: 12, color: "var(--color-text-secondary)" }}>
            {selectedIds.size} selected
            {leads.length > 0 && <> · {leads.length} of {total} visible</>}
          </div>
          <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "var(--color-text-secondary)", cursor: "pointer" }}>
            <input
              type="checkbox"
              checked={mode === "move"}
              onChange={(e) => setMode(e.target.checked ? "move" : "skip-existing")}
              style={{ accentColor: "var(--crm-primary)" }}
            />
            Move leads already in this pipeline
          </label>
          <div style={{ flex: 1 }} />
          <Button variant="secondary" size="md" onClick={onClose}>Cancel</Button>
          <Button
            variant="primary"
            size="md"
            onClick={submit}
            disabled={bulkAdd.isPending || selectedIds.size === 0 || !targetStageId}
            leftIcon={bulkAdd.isPending ? <Loader2 size={13} className="animate-spin" /> : null}
          >
            {bulkAdd.isPending ? "Adding…" : `Add ${selectedIds.size || ""}`}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
